import React, { useState, useEffect, useCallback, useMemo } from "react"
import { Box, Chip, Typography, Paper, List, ListItem, ListItemText, ListItemIcon, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Stack, ToggleButton, ToggleButtonGroup, FormControlLabel, Switch, Alert, LinearProgress, IconButton, Checkbox } from "@mui/material"
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import EditIcon from '@mui/icons-material/Edit'
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs"
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro"
import dayjs from 'dayjs'
import WeekGrid, { DAYS_FULL } from "../../Schedule/WeekGrid"
import { getCaseStatusColor } from "../../Schedule/caseStatusColor"
import RecurrenceEditor from "./RecurrenceEditor"
import { GetSchedule, GetScheduleTemplates, GetUnscheduledStudies, BookSlot, UnbookSlot, ReorderCases, GetOrgChildren, CreateScheduleTemplate, UpdateScheduleTemplate, RemoveScheduleTemplate, UpsertScheduleOverride, RemoveSlotFromOverride, MoveSlot, UpdateSlot, GetStudy, GetToken, MwlEdit, UpdateOrgSlotColor, Subscribe, Unsubscribe } from "../../../../api"
import { useNavigate } from "react-router-dom"
import DetailPanelContent from '../shared/DetailPanelContent'
import PdfPreview from '../shared/PdfPreview'
import ReportingData from '../Studies/Teleconsulation/Respond/ReportingData'
import { ModalityWorklistFields } from '../shared/ModalityWorklist'

const DEFAULT_COLORS = ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#cddc39', '#ffc107', '#ff9800', '#ff5722', '#795548', '#607d8b']

const ordinal = (n) => { const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]) }
const utcDow = (dateStr) => { const [y, m, d] = dateStr.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).getUTCDay() }
const utcDayOfMonth = (dateStr) => Number(dateStr.split('-')[2])
const getWeekOfMonth = (dateStr) => { const [y, m, day] = dateStr.split('-').map(Number); const dow = new Date(Date.UTC(y, m - 1, day)).getUTCDay(); let count = 0; for (let i = 1; i <= day; i++) { if (new Date(Date.UTC(y, m - 1, i)).getUTCDay() === dow) count++ } return count }
const findTemplateForSlot = (slot, day, templates) => { if (!slot?.templateId || !day?.templates?.length) return null; return templates.find(t => t.id === slot.templateId) || null }

const ScheduleCalendar = ({ access, hasSchedule, socketStatus }) => {
    const [templates, setTemplates] = useState([])
    const [schedule, setSchedule] = useState({ days: [] })
    const [weekStart, setWeekStart] = useState(new Date())
    const [unscheduled, setUnscheduled] = useState([])

    const navigate = useNavigate()
    const canEdit = !!(access.org.settings?.scheduler && access.admin)
    const orgId = access.org.id
    const isParent = !!access.org.settings?.scheduler

    const [childOrgs, setChildOrgs] = useState([])
    const [clinicColors, setClinicColors] = useState({})
    const [slotDialog, setSlotDialog] = useState(null)
    const [editDialog, setEditDialog] = useState(null)
    const [deleteDialog, setDeleteDialog] = useState(null)
    const [dayMaxDialog, setDayMaxDialog] = useState(null)
    const [slotForm, setSlotForm] = useState({ childOrg: '', startTime: '09:00', endTime: '12:00', maxCases: '', color: '', notes: '' })
    const [isRecurring, setIsRecurring] = useState(false)
    const [recForm, setRecForm] = useState({ pattern: 'weekly', interval: 1, daysOfWeek: [], monthlyMode: 'weekOfMonth', dayOfMonth: 1, weekOfMonth: 1 })
    const [dayMaxRecurring, setDayMaxRecurring] = useState(false)
    const [dayMaxRecForm, setDayMaxRecForm] = useState({ pattern: 'weekly', interval: 1, daysOfWeek: [], monthlyMode: 'weekOfMonth', dayOfMonth: 1, weekOfMonth: 1 })
    const [collapsed, setCollapsed] = useState(false)
    const [view, setView] = useState('week')
    const [selectedDate, setSelectedDate] = useState(null)
    const [studyDetailDialog, setStudyDetailDialog] = useState(null)
    const [slotDetailDialog, setSlotDetailDialog] = useState(null)
    const [warnDialog, setWarnDialog] = useState(null)
    const [mwlPromptDialog, setMwlPromptDialog] = useState(null)
    const [mwlData, setMwlData] = useState({})
    const mwlSlotDateTime = mwlPromptDialog?.slotDateTime ?? null
    const mwlInitialData = useMemo(() => mwlSlotDateTime ? {
        ScheduledStartDate: mwlSlotDateTime.split('T')[0].replace(/-/g, ''),
        ScheduledStartTime: mwlSlotDateTime.split('T')[1]?.replace(':', '') + '00'
    } : {}, [mwlSlotDateTime])
    const [selectionMode, setSelectionMode] = useState(false)
    const [selectedStudyIds, setSelectedStudyIds] = useState(new Set())
    const [bulkWarnDialog, setBulkWarnDialog] = useState(null)
    const [bulkErrorDialog, setBulkErrorDialog] = useState(null)

    const weekDates = useMemo(() => {
        const d = new Date(weekStart); d.setHours(12, 0, 0, 0)
        const sun = new Date(d); sun.setDate(d.getDate() - d.getDay())
        return {
            start: sun.toISOString().split('T')[0],
            end: new Date(sun.getTime() + 6 * 86400000).toISOString().split('T')[0]
        }
    }, [weekStart])

    const [loading, setLoading] = useState(false)

    const fetchAll = useCallback(() => {
        if (!hasSchedule) return Promise.resolve()
        const opts = isParent ? { filterOrg: orgId } : { childOrg: orgId }
        const templatePromise = isParent ? GetScheduleTemplates(orgId) : Promise.resolve([])
        setLoading(true)
        return Promise.all([templatePromise, GetSchedule(orgId, weekDates.start, weekDates.end, opts), GetUnscheduledStudies(orgId)])
            .then(([t, s, u]) => { setTemplates(t); setSchedule(s); setUnscheduled(u || []) })
            .catch(() => {})
            .finally(() => setLoading(false))
    }, [orgId, weekDates, hasSchedule, isParent])

    useEffect(() => {
        if (canEdit) GetOrgChildren(orgId).then(r => setChildOrgs(r.sort((a, b) => a.name.localeCompare(b.name))))
    }, [canEdit, orgId])

    useEffect(() => {
        const colors = {}
        for (const o of childOrgs) if (o.settings?.slotColor) colors[String(o.id)] = o.settings.slotColor
        setClinicColors(colors)
    }, [childOrgs])

    const slotToData = (s) => ({ childOrg: s.childOrg?.id || s.childOrg, startTime: s.startTime, endTime: s.endTime, maxCases: s.maxCases, color: s.color, notes: s.notes })

    useEffect(() => { fetchAll() }, [fetchAll, socketStatus])

    useEffect(() => {
        Subscribe({ coll: 'schedule', type: orgId }, fetchAll)
        return () => socketStatus && Unsubscribe({ coll: 'schedule', type: orgId })
    }, [orgId, fetchAll, socketStatus])

    // --- Slot creation and day max ---
    const saveSlot = async () => {
        const { date, pendingStudyId } = slotDialog
        const slot = { childOrg: slotForm.childOrg, startTime: slotForm.startTime, endTime: slotForm.endTime, maxCases: slotForm.maxCases === '' ? null : Number(slotForm.maxCases), color: slotForm.color || null, notes: slotForm.notes || null }
        if (slotForm.childOrg && slotForm.color) {
            setClinicColors(c => ({ ...c, [slotForm.childOrg]: slotForm.color }))
            setChildOrgs(orgs => orgs.map(o => o.id === slotForm.childOrg ? { ...o, settings: { ...o.settings, slotColor: slotForm.color } } : o))
            UpdateOrgSlotColor(slotForm.childOrg, slotForm.color).catch(() => {})
        }
        if (isRecurring) {
            const daysOfWeek = recForm.daysOfWeek.length ? recForm.daysOfWeek : [utcDow(date)]
            const recurrence = { pattern: recForm.pattern, interval: recForm.pattern === 'weekly' ? recForm.interval : 1, startDate: date, ...(recForm.pattern === 'monthly' ? { monthlyMode: recForm.monthlyMode, dayOfMonth: recForm.monthlyMode === 'dayOfMonth' ? recForm.dayOfMonth : undefined, weekOfMonth: recForm.monthlyMode === 'weekOfMonth' ? recForm.weekOfMonth : undefined } : {}) }
            await CreateScheduleTemplate(orgId, { daysOfWeek, slots: [slot], recurrence })
        } else {
            await UpsertScheduleOverride(orgId, { date, templateId: null, slots: [slot] })
            if (pendingStudyId) {
                const refreshed = await GetSchedule(orgId, weekDates.start, weekDates.end, isParent ? { filterOrg: orgId } : { childOrg: orgId })
                const createdSlot = refreshed?.days?.find(d => d.date === date)?.slots?.find(s => s.startTime === slot.startTime && !s.templateId)
                if (createdSlot) {
                    await BookSlot({ date, templateId: null, slotId: String(createdSlot._id || createdSlot.id), caseId: pendingStudyId })
                    const study = unscheduled.find(s => (s.id || s._id) === pendingStudyId)
                    promptMwlIfNeeded([study], date, slot.startTime)
                }
            }
        }
        setSlotDialog(null); await fetchAll()
    }

    const editSlot = async (scope) => {
        const { slot, day, date } = editDialog
        const updates = {
            startTime: slot._startTime || slot.startTime,
            endTime: slot._endTime || slot.endTime,
            maxCases: slot._maxCases !== undefined ? (slot._maxCases === '' ? null : Number(slot._maxCases)) : slot.maxCases,
            color: slot._color !== undefined ? slot._color : slot.color,
            notes: slot._notes !== undefined ? slot._notes : slot.notes
        }
        const tmpl = findTemplateForSlot(slot, day, templates)
        const sid = String(slot._id || slot.id)
        if (scope === 'series' && tmpl) {
            await UpdateScheduleTemplate(orgId, { templateId: tmpl.id, slots: tmpl.slots.map(s => String(s._id || s.id) === sid ? { ...slotToData(s), ...updates } : slotToData(s)) })
        } else if (tmpl) {
            const currentSlots = (day?.slots || []).filter(s => s.templateId === tmpl.id)
            const slotsToUse = currentSlots.length ? currentSlots : tmpl.slots
            const slotToDataWithCases = (s) => ({ ...slotToData(s), cases: (s.cases || []).map(c => c?.id || c?._id || c) })
            await UpsertScheduleOverride(orgId, { date, templateId: tmpl.id, slots: slotsToUse.map(s => String(s._id || s.id) === sid ? { ...slotToDataWithCases(s), ...updates } : slotToDataWithCases(s)) })
            if (updates.startTime && updates.startTime !== slot.startTime)
                updateMwlForCases(slot.cases || [], date, updates.startTime)
        } else {
            const sid = String(slot._id || slot.id)
            const newDate = slot._date || date
            if (newDate !== date) await MoveSlot(orgId, { slotId: sid, fromDate: date, toDate: newDate })
            await UpdateSlot(orgId, { slotId: sid, ...updates })
            const effectiveDate = newDate
            const effectiveTime = updates.startTime || slot.startTime
            if (newDate !== date || (updates.startTime && updates.startTime !== slot.startTime))
                updateMwlForCases(slot.cases || [], effectiveDate, effectiveTime)
        }
        setEditDialog(null); await fetchAll()
    }

    const deleteSlot = async (scope) => {
        const { slot, day, date } = deleteDialog
        const tmpl = findTemplateForSlot(slot, day, templates)
        const sid = String(slot._id || slot.id)
        if (scope === 'series' && tmpl) {
            const newSlots = tmpl.slots.filter(s => String(s._id || s.id) !== sid).map(slotToData)
            newSlots.length === 0 ? await RemoveScheduleTemplate(orgId, { templateId: tmpl.id, removeOverrides: true }) : await UpdateScheduleTemplate(orgId, { templateId: tmpl.id, slots: newSlots })
        } else if (tmpl) {
            await UpsertScheduleOverride(orgId, { date, templateId: tmpl.id, skip: true, slots: [] })
        } else {
            await RemoveSlotFromOverride(orgId, sid)
        }
        setDeleteDialog(null); await fetchAll()
    }

    const saveDayMax = async () => {
        const { date, templateId } = dayMaxDialog
        const val = dayMaxDialog.currentMax === '' ? null : Number(dayMaxDialog.currentMax)
        if (dayMaxRecurring) {
            if (templateId) {
                await UpdateScheduleTemplate(orgId, { templateId, dayMax: val })
            } else {
                const daysOfWeek = dayMaxRecForm.daysOfWeek.length ? dayMaxRecForm.daysOfWeek : [utcDow(date)]
                const recurrence = { pattern: dayMaxRecForm.pattern, interval: dayMaxRecForm.pattern === 'weekly' ? dayMaxRecForm.interval : 1, startDate: date, ...(dayMaxRecForm.pattern === 'monthly' ? { monthlyMode: dayMaxRecForm.monthlyMode, dayOfMonth: dayMaxRecForm.dayOfMonth, weekOfMonth: dayMaxRecForm.weekOfMonth } : {}) }
                await CreateScheduleTemplate(orgId, { daysOfWeek, dayMax: val, slots: [], recurrence })
            }
        } else {
            await UpsertScheduleOverride(orgId, { date, templateId: null, dayMax: val })
            // If any matching templates have a dayMax that would cap below the new value, clear them
            if (val !== null) {
                const day = schedule.days?.find(d => d.date === date)
                const matchedTemplateIds = new Set(day?.templates || [])
                const blocking = templates.filter(t => matchedTemplateIds.has(t.id) && t.dayMax != null && t.dayMax < val)
                await Promise.all(blocking.map(t => UpdateScheduleTemplate(orgId, { templateId: t.id, dayMax: null })))
            }
        }
        setDayMaxDialog(null); fetchAll()
    }

    const isFromTemplate = (slot) => !!(slot?.templateId && !slot?.isOverridden)

    const handleSlotClick = (slot, day, dateStr) => setSlotDetailDialog({ slot, day, date: dateStr })

    const handleDayHeaderClick = (dateStr, dow, day) => {
        if (!canEdit) { setView('day'); setSelectedDate(dateStr); return }
        setDayMaxRecurring(false)
        const matched = day?.templates?.length ? templates.find(t => day.templates.includes(t.id) && t.dayMax != null) || templates.find(t => day.templates.includes(t.id)) : null
        setDayMaxRecForm({ pattern: matched?.recurrence?.pattern || 'weekly', interval: matched?.recurrence?.interval || 1, daysOfWeek: matched?.daysOfWeek || [dow], monthlyMode: matched?.recurrence?.monthlyMode || 'weekOfMonth', dayOfMonth: matched?.recurrence?.dayOfMonth || utcDayOfMonth(dateStr), weekOfMonth: matched?.recurrence?.weekOfMonth || getWeekOfMonth(dateStr) })
        setDayMaxDialog({ date: dateStr, dayOfWeek: dow, currentMax: day?.dayMax ?? matched?.dayMax ?? '', templateId: matched?.id })
    }

    const handleEmptyClick = (dateStr, _dow, time) => {
        const dow = utcDow(dateStr)
        const endH = Math.min(parseInt(time.split(':')[0]) + 3, 22)
        setSlotForm({ childOrg: '', startTime: time, endTime: `${String(endH).padStart(2, '0')}:${time.split(':')[1]}`, maxCases: '', color: DEFAULT_COLORS[0], notes: '' })
        setIsRecurring(false)
        setRecForm({ pattern: 'weekly', interval: 1, daysOfWeek: [dow], monthlyMode: 'weekOfMonth', dayOfMonth: utcDayOfMonth(dateStr), weekOfMonth: getWeekOfMonth(dateStr) })
        setSlotDialog({ date: dateStr, dayOfWeek: dow })
    }

    const editTemplateDesc = useMemo(() => {
        if (!editDialog) return ''
        const t = findTemplateForSlot(editDialog.slot, editDialog.day, templates)
        if (!t?.recurrence) return ''
        const rec = t.recurrence
        const days = (t.daysOfWeek || []).map(d => DAYS_FULL[d]).join(', ')
        if (rec.pattern === 'weekly') return rec.interval === 1 ? `Weekly on ${days}` : `Every ${rec.interval} weeks on ${days}`
        if (rec.pattern === 'monthly') {
            if (rec.monthlyMode === 'dayOfMonth') return `Monthly on the ${ordinal(rec.dayOfMonth)}`
            if (rec.monthlyMode === 'weekOfMonth') return `Monthly on the ${ordinal(rec.weekOfMonth)} ${days}`
        }
        return ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editDialog, templates])

    const handleEmptyDrop = (studyId, dateStr, time) => {
        const dow = utcDow(dateStr)
        const [h, m] = time.split(':').map(Number)
        const endMins = h * 60 + m + 30
        const endTime = `${String(Math.floor(endMins / 60)).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`
        const study = unscheduled.find(s => (s.id || s._id) === studyId)
        const associateOrgId = study?.associateOrg?.id || study?.associateOrg?._id || study?.associateOrg
        const defaultClinic = associateOrgId && childOrgs.find(o => o.id === String(associateOrgId)) ? String(associateOrgId) : ''
        setSlotForm({ childOrg: defaultClinic, startTime: time, endTime, maxCases: '', color: clinicColors[defaultClinic] || DEFAULT_COLORS[0], notes: '' })
        setIsRecurring(false)
        setRecForm({ pattern: 'weekly', interval: 1, daysOfWeek: [dow], monthlyMode: 'weekOfMonth', dayOfMonth: utcDayOfMonth(dateStr), weekOfMonth: getWeekOfMonth(dateStr) })
        setSlotDialog({ date: dateStr, dayOfWeek: dow, pendingStudyId: studyId })
    }

    const mwlEnabled = !!access.org.settings?.modalityWorklist
    const aetitle = Array.isArray(access.org.aetitle) ? access.org.aetitle[0] : access.org.aetitle

    const toMwlDateTime = (dateStr, timeStr) => ({
        ScheduledStartDate: dateStr.replace(/-/g, ''),
        ScheduledStartTime: timeStr.replace(':', '') + '00'
    })

    const updateMwlForCases = useCallback((cases, dateStr, startTime) => {
        if (!mwlEnabled) return
        const dt = toMwlDateTime(dateStr, startTime)
        for (const c of cases) {
            if (c?.modalityWorklist)
                MwlEdit(c.id || c._id, { ...dt, aetitle }).catch(() => {})
        }
    }, [mwlEnabled, aetitle])

    const promptMwlIfNeeded = useCallback((studies, slotDate, slotTime) => {
        const arr = Array.isArray(studies) ? studies : [studies]
        const eligible = arr.filter(s => s && !s.modalityWorklist)
        if (!mwlEnabled || !eligible.length) return
        const slotDateTime = `${slotDate}T${slotTime}`
        setMwlData({})
        setMwlPromptDialog({ studies: eligible, slotDateTime })
    }, [mwlEnabled])

    const findStudyAnywhere = useCallback((studyId) => {
        const fromUnscheduled = unscheduled.find(s => (s.id || s._id) === studyId)
        if (fromUnscheduled) return fromUnscheduled
        for (const day of (schedule.days || []))
            for (const slot of (day.slots || []))
                for (const c of (slot.cases || []))
                    if ((c?.id || c?._id) === studyId) return c
        return null
    }, [unscheduled, schedule])

    // --- Drag and drop ---
    const handleSlotDrop = useCallback(async ({ slotId, fromDate, templateId, overrideId }, toDate) => {
        if (fromDate === toDate) return
        const fromDay = schedule.days?.find(d => d.date === fromDate)
        const movingSlot = fromDay?.slots?.find(s => String(s._id || s.id) === slotId)
        try {
            await MoveSlot(orgId, { slotId, fromDate, toDate, templateId, overrideId })
            await fetchAll()
            if (movingSlot?.cases?.length) updateMwlForCases(movingSlot.cases, toDate, movingSlot.startTime)
        } catch (e) {}
    }, [orgId, fetchAll, schedule, updateMwlForCases])

    const handleDrop = useCallback(async (studyId, slot, dateStr) => {
        const study = findStudyAnywhere(studyId)
        const associateOrgId = study?.associateOrg?.id || study?.associateOrg?._id || String(study?.associateOrg || '')
        const slotOrgId = slot.childOrg?.id || slot.childOrg?._id || String(slot.childOrg || '')
        if (associateOrgId && slotOrgId && associateOrgId !== slotOrgId) {
            setWarnDialog({ studyId, slot, dateStr, slotOrgName: slot.childOrg?.name || 'this clinic' })
            return
        }
        try {
            await BookSlot({
                date: dateStr, templateId: slot.templateId,
                slotId: String(slot._id || slot.id || slot.slotId), caseId: studyId
            })
            fetchAll()
            if (study?.modalityWorklist) updateMwlForCases([study], dateStr, slot.startTime)
            else promptMwlIfNeeded([study], dateStr, slot.startTime)
        } catch (e) {}
    }, [fetchAll, findStudyAnywhere, updateMwlForCases, promptMwlIfNeeded])

    const handleBulkDrop = useCallback(async (studyIds, slot, dateStr) => {
        const day = schedule.days?.find(d => d.date === dateStr)
        const dayTotalCases = (day?.slots || []).reduce((sum, s) => sum + (s.cases?.length || 0), 0)
        const slotId = String(slot._id || slot.id || slot.slotId)
        if (slot.maxCases != null && (slot.caseCount + studyIds.length) > slot.maxCases) {
            setBulkErrorDialog(`This slot only has ${slot.maxCases - slot.caseCount} spot${slot.maxCases - slot.caseCount === 1 ? '' : 's'} remaining.`)
            return
        }
        if (day?.dayMax != null && (dayTotalCases + studyIds.length) > day.dayMax) {
            setBulkErrorDialog(`This day only has ${day.dayMax - dayTotalCases} spot${day.dayMax - dayTotalCases === 1 ? '' : 's'} remaining.`)
            return
        }
        const studies = studyIds.map(id => findStudyAnywhere(id)).filter(Boolean)
        const slotOrgId = slot.childOrg?.id || slot.childOrg?._id || String(slot.childOrg || '')
        const mismatched = studies.filter(s => {
            const assocId = s?.associateOrg?.id || s?.associateOrg?._id || String(s?.associateOrg || '')
            return assocId && slotOrgId && assocId !== slotOrgId
        })
        if (mismatched.length) {
            setBulkWarnDialog({ studyIds, studies, slot, dateStr, mismatchCount: mismatched.length })
            return
        }
        try {
            let currentSlotId = slotId
            for (const id of studyIds) {
                const result = await BookSlot({ date: dateStr, templateId: slot.templateId, slotId: currentSlotId, caseId: id })
                if (result?.slotId) currentSlotId = result.slotId
            }
            setSelectionMode(false)
            setSelectedStudyIds(new Set())
            await fetchAll()
            const withMwl = studies.filter(s => s?.modalityWorklist)
            if (withMwl.length) updateMwlForCases(withMwl, dateStr, slot.startTime)
            promptMwlIfNeeded(studies, dateStr, slot.startTime)
        } catch (e) {}
    }, [fetchAll, findStudyAnywhere, schedule, updateMwlForCases, promptMwlIfNeeded])

    const executeBulkBook = useCallback(async (studyIds, studies, slot, slotId, dateStr) => {
        try {
            let currentSlotId = slotId
            for (const id of studyIds) {
                const result = await BookSlot({ date: dateStr, templateId: slot.templateId, slotId: currentSlotId, caseId: id })
                if (result?.slotId) currentSlotId = result.slotId
            }
            setSelectionMode(false)
            setSelectedStudyIds(new Set())
            await fetchAll()
            const withMwl = studies.filter(s => s?.modalityWorklist)
            if (withMwl.length) updateMwlForCases(withMwl, dateStr, slot.startTime)
            promptMwlIfNeeded(studies, dateStr, slot.startTime)
        } catch (e) {}
    }, [fetchAll, updateMwlForCases, promptMwlIfNeeded])

    const handleCaseClick = useCallback(async (caseObj) => {
        const caseId = caseObj?.id || caseObj?._id || String(caseObj)
        try {
            await UnbookSlot({ caseId })
            fetchAll()
        } catch (e) {}
    }, [fetchAll])

    // --- Render cases as draggable chips with patient names ---
    const renderCases = useCallback((cases, { onCaseClick, slot, dateStr } = {}) =>
        cases.map((c, i) => {
            const caseId = c?.id || c?._id || String(c)
            const name = c?.PatientMainDicomTags?.PatientName || `Case ${i + 1}`
            const bgcolor = getCaseStatusColor(c)
            return (
                <Chip key={caseId} label={name} size="small"
                    draggable
                    onDragStart={e => {
                        e.stopPropagation()
                        e.dataTransfer.setData('text/plain', JSON.stringify({ studyId: caseId }))
                    }}
                    onClick={e => {
                        e.stopPropagation()
                        setStudyDetailDialog({ studyId: caseId, study: c, showPdf: false, pdfStudyId: null })
                        GetStudy(caseId, { org: orgId }).then(study => setStudyDetailDialog(d => d?.studyId === caseId ? { ...d, study } : d)).catch(() => {})
                    }}
                    onDoubleClick={e => { e.stopPropagation(); onCaseClick?.(c, slot, dateStr) }}
                    sx={{
                        fontSize: 9, height: 16, bgcolor, color: '#fff',
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.8 }
                    }} />
            )
        })
    , [orgId])

    if (!hasSchedule) return null

    return (
        <>
        <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Unscheduled studies panel */}
            {collapsed ? (
                <Paper variant="outlined" sx={{ width: 28, flexShrink: 0, maxHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', overflow: 'hidden' }} onClick={() => setCollapsed(false)}>
                    <IconButton size="small" sx={{ mt: 0.5 }}><ChevronRightIcon fontSize="small" /></IconButton>
                    <Typography variant="caption" sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'text.secondary', mt: 1, userSelect: 'none' }}>Unscheduled</Typography>
                </Paper>
            ) : (
            <Box sx={{ width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="outlined" size="small" fullWidth onClick={() => navigate('/mobile-consult')}>+ Add New</Button>
                <Paper variant="outlined" sx={{ flex: 1, maxHeight: 'calc(100vh - 160px)', overflow: 'auto' }}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }}
                onDrop={e => {
                    e.preventDefault()
                    try {
                        const data = JSON.parse(e.dataTransfer.getData('text/plain'))
                        if (data.studyId) handleCaseClick({ id: data.studyId })
                    } catch (_) {}
                }}>
                <Box sx={{ p: 1, bgcolor: 'action.hover', position: 'sticky', top: 0, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {selectionMode ? (
                        <>
                            <Checkbox size="small" sx={{ p: 0, mr: 0.5 }}
                                checked={selectedStudyIds.size === unscheduled.length && unscheduled.length > 0}
                                indeterminate={selectedStudyIds.size > 0 && selectedStudyIds.size < unscheduled.length}
                                onChange={e => setSelectedStudyIds(e.target.checked ? new Set(unscheduled.map(s => s.id)) : new Set())} />
                            <Typography variant="subtitle2" sx={{ flex: 1 }}>{selectedStudyIds.size} selected</Typography>
                            <Button size="small" onClick={() => { setSelectionMode(false); setSelectedStudyIds(new Set()) }}>Done</Button>
                        </>
                    ) : (
                        <>
                            <Typography variant="subtitle2" sx={{ flex: 1 }}>Unscheduled ({unscheduled.length})</Typography>
                            {unscheduled.length > 0 && <Button size="small" onClick={() => setSelectionMode(true)}>Select</Button>}
                            <IconButton size="small" onClick={() => setCollapsed(true)}><ChevronLeftIcon fontSize="small" /></IconButton>
                        </>
                    )}
                </Box>
                <List dense disablePadding>
                    {(() => {
                        const getMod = s => s.modalityWorklist?.ScheduledProcedureStepSequence?.[0]?.Modality
                        const noMwl = unscheduled.filter(s => !getMod(s))
                        const byModality = unscheduled.filter(s => getMod(s)).reduce((acc, s) => {
                            const mod = getMod(s)
                            ;(acc[mod] = acc[mod] || []).push(s)
                            return acc
                        }, {})
                        const renderStudy = study => {
                            const name = study.PatientMainDicomTags?.PatientName || study.userData?.PatientMainDicomTags?.PatientName || 'Unknown'
                            const desc = study.MainDicomTags?.StudyDescription || study.userData?.MainDicomTags?.StudyDescription || ''
                            const pid = study.PatientMainDicomTags?.PatientID || study.userData?.PatientMainDicomTags?.PatientID || ''
                            const orgName = study.associateOrg?.name
                            const isSelected = selectedStudyIds.has(study.id)
                            return (
                                <React.Fragment key={study.id}>
                                    <ListItem
                                        draggable={selectionMode ? selectedStudyIds.size > 0 && isSelected : true}
                                        onDragStart={e => {
                                            if (selectionMode) {
                                                if (!isSelected) { e.preventDefault(); return }
                                                e.dataTransfer.setData('text/plain', JSON.stringify({ studyIds: [...selectedStudyIds] }))
                                            } else {
                                                e.dataTransfer.setData('text/plain', JSON.stringify({ studyId: study.id }))
                                            }
                                        }}
                                        onClick={() => {
                                            if (selectionMode) {
                                                setSelectedStudyIds(prev => { const next = new Set(prev); isSelected ? next.delete(study.id) : next.add(study.id); return next })
                                                return
                                            }
                                            setStudyDetailDialog({ studyId: study.id, study, showPdf: false, pdfStudyId: null })
                                            GetStudy(study.id, { org: orgId }).then(s => setStudyDetailDialog(d => d?.studyId === study.id ? { ...d, study: s } : d)).catch(() => {})
                                        }}
                                        sx={{
                                            cursor: selectionMode ? 'pointer' : 'grab',
                                            '&:active': { cursor: selectionMode ? 'pointer' : 'grabbing' },
                                            '&:hover': { bgcolor: 'action.hover' }, py: 0.5,
                                            bgcolor: isSelected ? 'action.selected' : 'transparent'
                                        }}>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            {selectionMode
                                                ? <Checkbox size="small" sx={{ p: 0 }} checked={isSelected} onChange={() => {}} />
                                                : <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={name}
                                            secondary={
                                                <>
                                                    {orgName && <Typography component="span" display="block" sx={{ fontSize: 10 }}>{orgName}</Typography>}
                                                    {[pid, desc].filter(Boolean).join(' · ')}
                                                </>
                                            }
                                            primaryTypographyProps={{ fontSize: 12, noWrap: true }}
                                            secondaryTypographyProps={{ fontSize: 10, noWrap: true, component: 'div' }}
                                        />
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            )
                        }
                        return (
                            <>
                                {noMwl.map(renderStudy)}
                                {Object.entries(byModality).map(([mod, studies]) => (
                                    <React.Fragment key={mod}>
                                        <Typography variant="caption" sx={{ px: 1, py: 0.5, display: 'block', bgcolor: 'action.hover', color: 'text.secondary', fontWeight: 600, letterSpacing: 0.5 }}>{mod}</Typography>
                                        {studies.map(renderStudy)}
                                    </React.Fragment>
                                ))}
                            </>
                        )
                    })()}
                    {!unscheduled.length && (
                        <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block', textAlign: 'center' }}>
                            All studies scheduled
                        </Typography>
                    )}
                </List>
            </Paper>
            </Box>
            )}

            {/* Calendar */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {loading && <LinearProgress sx={{ mb: 1 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                    <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
                        <ToggleButton value="week">Week</ToggleButton>
                        <ToggleButton value="day">Day</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <WeekGrid
                    schedule={schedule}
                    templates={templates}
                    weekStart={weekStart}
                    onWeekChange={(d) => { setWeekStart(d); setSelectedDate(d.toISOString().split('T')[0]) }}
                    renderCases={renderCases}
                    onDrop={handleDrop}
                    onBulkDrop={handleBulkDrop}
                    onCaseClick={handleCaseClick}
                    onSlotClick={handleSlotClick}
                    isParent={isParent}
                    view={view}
                    selectedDate={selectedDate || new Date().toISOString().split('T')[0]}
                    {...(canEdit ? {
                        editable: true,
                        onSlotDrop: handleSlotDrop,
                        onDayHeaderClick: handleDayHeaderClick,
                        onEmptyClick: handleEmptyClick,
                        onEmptyDrop: handleEmptyDrop
                    } : {})}
                />
            </Box>
        </Box>

        {/* Slot Detail — visible to all orgs */}
            <Dialog open={!!slotDetailDialog} onClose={() => setSlotDetailDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {isParent ? slotDetailDialog?.slot?.childOrg?.name : slotDetailDialog?.slot?.orgName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {slotDetailDialog?.date ? new Date(slotDetailDialog.date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
                            {' · '}{slotDetailDialog?.slot?.startTime} – {slotDetailDialog?.slot?.endTime}
                        </Typography>
                    </Box>
                    {canEdit && (
                        <IconButton size="small" onClick={() => { setEditDialog(slotDetailDialog); setSlotDetailDialog(null) }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Capacity: <strong>{slotDetailDialog?.slot?.caseCount ?? 0}/{slotDetailDialog?.slot?.maxCases ?? '∞'}</strong>
                            </Typography>
                            {slotDetailDialog?.slot?.color && (
                                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: slotDetailDialog.slot.color, mt: '3px' }} />
                            )}
                        </Box>
                        {slotDetailDialog?.slot?.notes && (
                            <Typography variant="body2" color="text.secondary">{slotDetailDialog.slot.notes}</Typography>
                        )}
                        {slotDetailDialog?.slot?.cases?.length > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Cases</Typography>
                                {(() => {
                                    const getMod = c => c?.modalityWorklist?.ScheduledProcedureStepSequence?.[0]?.Modality
                                    const cases = slotDetailDialog.slot.cases
                                    const noMwl = cases.filter(c => !getMod(c))
                                    const byModality = cases.filter(c => getMod(c)).reduce((acc, c) => {
                                        const mod = getMod(c)
                                        ;(acc[mod] = acc[mod] || []).push(c)
                                        return acc
                                    }, {})
                                    const handleCaseReorderDrop = async (draggedId, targetId, after) => {
                                        const ordered = [...cases]
                                        const fromIdx = ordered.findIndex(c => (c?.id || c?._id) === draggedId)
                                        if (fromIdx === -1) return
                                        const [moved] = ordered.splice(fromIdx, 1)
                                        const toIdx = ordered.findIndex(c => (c?.id || c?._id) === targetId)
                                        ordered.splice(after ? toIdx + 1 : toIdx, 0, moved)
                                        const caseIds = ordered.map(c => c?.id || c?._id || String(c))
                                        setSlotDetailDialog(d => ({ ...d, slot: { ...d.slot, cases: ordered } }))
                                        await ReorderCases({ slotId: slotDetailDialog.slot.slotId, caseIds }).catch(() => {})
                                        fetchAll()
                                    }
                                    const renderCase = (c, i, groupCases) => {
                                        const caseId = c?.id || c?._id || String(c)
                                        const name = c?.PatientMainDicomTags?.PatientName || `Case ${i + 1}`
                                        const pid = c?.PatientMainDicomTags?.PatientID || ''
                                        const desc = c?.MainDicomTags?.StudyDescription || ''
                                        const canReorder = !c?.completeBy && !(c?.instanceCount > 0)
                                        return (
                                            <Box key={caseId}
                                                draggable={canReorder}
                                                onDragStart={canReorder ? (e => { e.stopPropagation(); e.dataTransfer.setData('text/plain', JSON.stringify({ reorderCaseId: caseId })) }) : undefined}
                                                onDragOver={e => { e.preventDefault(); e.stopPropagation()
                                                    const rect = e.currentTarget.getBoundingClientRect()
                                                    const after = e.clientY > rect.top + rect.height / 2
                                                    setSlotDetailDialog(d => ({ ...d, _dropTarget: caseId, _dropAfter: after }))
                                                }}
                                                onDragLeave={() => setSlotDetailDialog(d => ({ ...d, _dropTarget: null }))}
                                                onDrop={e => { e.preventDefault(); e.stopPropagation()
                                                    const data = JSON.parse(e.dataTransfer.getData('text/plain'))
                                                    if (!data.reorderCaseId || data.reorderCaseId === caseId) return
                                                    const rect = e.currentTarget.getBoundingClientRect()
                                                    const after = e.clientY > rect.top + rect.height / 2
                                                    setSlotDetailDialog(d => ({ ...d, _dropTarget: null }))
                                                    handleCaseReorderDrop(data.reorderCaseId, caseId, after)
                                                }}
                                                sx={{ position: 'relative' }}>
                                                {slotDetailDialog._dropTarget === caseId && !slotDetailDialog._dropAfter && (
                                                    <Box sx={{ height: 2, bgcolor: 'primary.main', borderRadius: 1, mx: 1, mb: '1px' }} />
                                                )}
                                                <ListItem disablePadding
                                                    onClick={() => {
                                                        setStudyDetailDialog({ studyId: caseId, study: c, showPdf: false, pdfStudyId: null })
                                                        GetStudy(caseId, { org: orgId }).then(s => setStudyDetailDialog(d => d?.studyId === caseId ? { ...d, study: s } : d)).catch(() => {})
                                                    }}
                                                    sx={{ cursor: canReorder ? 'grab' : 'pointer', borderRadius: 1, borderLeft: 3, borderColor: getCaseStatusColor(c), pl: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                                                    <ListItemText
                                                        primary={name}
                                                        secondary={`${pid}${desc ? ' · ' + desc : ''}`}
                                                        primaryTypographyProps={{ fontSize: 13 }}
                                                        secondaryTypographyProps={{ fontSize: 11 }}
                                                    />
                                                </ListItem>
                                                {slotDetailDialog._dropTarget === caseId && slotDetailDialog._dropAfter && (
                                                    <Box sx={{ height: 2, bgcolor: 'primary.main', borderRadius: 1, mx: 1, mt: '1px' }} />
                                                )}
                                            </Box>
                                        )
                                    }
                                    return (
                                        <List dense disablePadding>
                                            {noMwl.map((c, i) => renderCase(c, i, noMwl))}
                                            {Object.entries(byModality).map(([mod, mCases]) => (
                                                <React.Fragment key={mod}>
                                                    <Typography variant="caption" sx={{ px: 1, py: 0.5, display: 'block', bgcolor: 'action.hover', color: 'text.secondary', fontWeight: 600, letterSpacing: 0.5 }}>{mod}</Typography>
                                                    {mCases.map((c, i) => renderCase(c, i, mCases))}
                                                </React.Fragment>
                                            ))}
                                        </List>
                                    )
                                })()}
                            </Box>
                        )}
                        {(!slotDetailDialog?.slot?.cases?.length) && (
                            <Typography variant="body2" color="text.secondary">No cases scheduled</Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSlotDetailDialog(null)}>Close</Button>
                </DialogActions>
            </Dialog>

        {canEdit && <>

            {/* Add Slot */}
            <Dialog open={!!slotDialog} onClose={() => setSlotDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Slot</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker label="Date" value={slotDialog?.date ? dayjs(slotDialog.date) : null}
                                onChange={v => { if (!v) return; const val = v.format('YYYY-MM-DD'); const dow = utcDow(val); setSlotDialog(s => ({ ...s, date: val, dayOfWeek: dow })); setRecForm(f => ({ ...f, daysOfWeek: [dow], dayOfMonth: utcDayOfMonth(val), weekOfMonth: getWeekOfMonth(val) })) }} />
                        </LocalizationProvider>
                        <TextField select label="Clinic" value={slotForm.childOrg} onChange={e => { const id = e.target.value; setSlotForm(f => ({ ...f, childOrg: id, color: clinicColors[id] || f.color || DEFAULT_COLORS[0] })) }} required>
                            {childOrgs.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                        </TextField>
                        <Stack direction="row" spacing={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker label="Start" value={dayjs(`2000-01-01T${slotForm.startTime}`)} onChange={v => v && setSlotForm(f => ({ ...f, startTime: v.format('HH:mm') }))} sx={{ flex: 1 }} />
                                <TimePicker label="End" value={dayjs(`2000-01-01T${slotForm.endTime}`)} onChange={v => v && setSlotForm(f => ({ ...f, endTime: v.format('HH:mm') }))} sx={{ flex: 1 }} />
                            </LocalizationProvider>
                        </Stack>
                        <TextField label="Max Cases (blank = unlimited)" type="number" value={slotForm.maxCases} onChange={e => setSlotForm(f => ({ ...f, maxCases: e.target.value }))} />
                        <TextField select label="Color" value={slotForm.color || DEFAULT_COLORS[0]} onChange={e => setSlotForm(f => ({ ...f, color: e.target.value }))}>
                            {DEFAULT_COLORS.map((c, i) => <MenuItem key={c} value={c}><Box sx={{ width: 20, height: 20, bgcolor: c, borderRadius: 1, display: 'inline-block', mr: 1, verticalAlign: 'middle' }} />Color {i + 1}</MenuItem>)}
                        </TextField>
                        <TextField label="Notes" value={slotForm.notes} onChange={e => setSlotForm(f => ({ ...f, notes: e.target.value }))} multiline rows={2} />
                        {!slotDialog?.pendingStudyId && <>
                        <FormControlLabel control={<Switch checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} />} label="Recurring" />
                        {isRecurring && <RecurrenceEditor form={recForm} setForm={setRecForm} startDate={slotDialog?.date}
                            onStartDateChange={val => { const dow = utcDow(val); setSlotDialog(s => ({ ...s, date: val, dayOfWeek: dow })); setRecForm(f => ({ ...f, daysOfWeek: [dow], dayOfMonth: utcDayOfMonth(val), weekOfMonth: getWeekOfMonth(val) })) }} />}
                        </>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSlotDialog(null)}>Cancel</Button>
                    <Button onClick={saveSlot} variant="contained" disabled={!slotForm.childOrg || !slotDialog?.date || slotForm.endTime <= slotForm.startTime}>
                        {isRecurring ? 'Save Series' : 'Save This Day Only'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Slot */}
            <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Slot — {editDialog?.slot?.childOrg?.name}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {editTemplateDesc && <Typography variant="caption" color="text.secondary">Series: {editTemplateDesc}</Typography>}
                        {!findTemplateForSlot(editDialog?.slot, editDialog?.day, templates) && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker label="Date"
                                    value={editDialog ? dayjs(editDialog.slot._date || editDialog.date) : null}
                                    onChange={v => v && setEditDialog(d => ({ ...d, slot: { ...d.slot, _date: v.format('YYYY-MM-DD') } }))} />
                            </LocalizationProvider>
                        )}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker label="Start" value={editDialog ? dayjs(`2000-01-01T${editDialog.slot._startTime || editDialog.slot.startTime}`) : null} onChange={v => v && setEditDialog(d => ({ ...d, slot: { ...d.slot, _startTime: v.format('HH:mm') } }))} />
                            <TimePicker label="End" value={editDialog ? dayjs(`2000-01-01T${editDialog.slot._endTime || editDialog.slot.endTime}`) : null} onChange={v => v && setEditDialog(d => ({ ...d, slot: { ...d.slot, _endTime: v.format('HH:mm') } }))} />
                        </LocalizationProvider>
                        <TextField label="Max Cases (blank = unlimited)" type="number" value={editDialog?.slot?._maxCases ?? editDialog?.slot?.maxCases ?? ''}
                            onChange={e => setEditDialog(d => ({ ...d, slot: { ...d.slot, _maxCases: e.target.value } }))} />
                        <TextField select label="Color"
                            value={editDialog?.slot?._color ?? editDialog?.slot?.color ?? DEFAULT_COLORS[0]}
                            onChange={e => setEditDialog(d => ({ ...d, slot: { ...d.slot, _color: e.target.value } }))}>
                            {DEFAULT_COLORS.map((c, i) => <MenuItem key={c} value={c}><Box sx={{ width: 20, height: 20, bgcolor: c, borderRadius: 1, display: 'inline-block', mr: 1, verticalAlign: 'middle' }} />Color {i + 1}</MenuItem>)}
                        </TextField>
                        <TextField label="Notes" multiline rows={2}
                            value={editDialog?.slot?._notes ?? editDialog?.slot?.notes ?? ''}
                            onChange={e => setEditDialog(d => ({ ...d, slot: { ...d.slot, _notes: e.target.value } }))} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => { setDeleteDialog(editDialog); setEditDialog(null) }}>Delete</Button>
                    <Button onClick={() => setEditDialog(null)}>Cancel</Button>
                    {isFromTemplate(editDialog?.slot)
                        ? <><Button onClick={() => editSlot('override')}>This Day Only</Button><Button variant="contained" onClick={() => editSlot('series')}>All Occurrences</Button></>
                        : <Button variant="contained" onClick={() => editSlot('override')}>Save</Button>}
                </DialogActions>
            </Dialog>

            {/* Delete Slot */}
            <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs">
                <DialogTitle>Delete Slot — {deleteDialog?.slot?.childOrg?.name}</DialogTitle>
                <DialogContent>
                    {deleteDialog?.slot?.caseCount > 0 && (
                        <Typography color="error" sx={{ mb: 1 }}>Warning: This slot has {deleteDialog.slot.caseCount} case{deleteDialog.slot.caseCount !== 1 ? 's' : ''} scheduled. Deleting will unschedule them.</Typography>
                    )}
                    {isFromTemplate(deleteDialog?.slot)
                        ? <Typography>Remove from just this day, or the entire recurring series?</Typography>
                        : <Typography>Remove this slot?</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
                    {isFromTemplate(deleteDialog?.slot)
                        ? <><Button color="error" onClick={() => deleteSlot('override')}>This Day Only</Button><Button color="error" variant="contained" onClick={() => deleteSlot('series')}>Entire Series</Button></>
                        : <Button color="error" variant="contained" onClick={() => deleteSlot('override')}>Delete</Button>}
                </DialogActions>
            </Dialog>

            {/* Clinic Mismatch Warning */}
            <Dialog open={!!warnDialog} onClose={() => setWarnDialog(null)} maxWidth="xs">
                <DialogTitle>Clinic Mismatch</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        This study is associated with a different clinic than <strong>{warnDialog?.slotOrgName}</strong>. Are you sure you want to schedule it here?
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWarnDialog(null)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={async () => {
                        const { studyId, slot, dateStr } = warnDialog
                        const study = findStudyAnywhere(studyId)
                        setWarnDialog(null)
                        try {
                            await BookSlot({ date: dateStr, templateId: slot.templateId, slotId: String(slot._id || slot.id || slot.slotId), caseId: studyId })
                            fetchAll()
                            if (study?.modalityWorklist) updateMwlForCases([study], dateStr, slot.startTime)
                            else promptMwlIfNeeded([study], dateStr, slot.startTime)
                        } catch (e) {}
                    }}>Schedule Anyway</Button>
                </DialogActions>
            </Dialog>

        </>}

        {/* Study Detail */}
            <Dialog open={!!studyDetailDialog} onClose={() => setStudyDetailDialog(null)} maxWidth="md" fullWidth>
                <DialogActions sx={{ pb: 0 }}>
                    {studyDetailDialog?.studyId && !studyDetailDialog?.study?.completeBy && (
                        <Button variant="contained" onClick={() => { setStudyDetailDialog(null); navigate(`/studies/${studyDetailDialog.studyId}/consult`) }}>Consult</Button>
                    )}
                    <Button onClick={() => setStudyDetailDialog(null)}>Close</Button>
                </DialogActions>
                <DialogContent sx={{ pt: 0 }}>
                    {studyDetailDialog?.study && (
                        <>
                            <ReportingData study={studyDetailDialog.study} section="consult" hideFreeze hideConsult sx={{ px: 1.5, pt: 2 }} />
                            <DetailPanelContent
                                org={orgId}
                                row={studyDetailDialog.study}
                                setPdfDialogState={({ studyId }) => setStudyDetailDialog(d => ({ ...d, showPdf: true, pdfStudyId: studyId }))}
                                medDream={(uid) => GetToken({ data: uid }).then(r => r.accessToken && window.open(`https://dream.pacs.corridor.vet/?token=${r.accessToken}`, '_blank')).catch(() => {})}
                            />
                        </>
                    )}
                    {studyDetailDialog?.showPdf && (
                        <PdfPreview open studyId={studyDetailDialog.pdfStudyId} setDialogState={() => setStudyDetailDialog(d => ({ ...d, showPdf: false }))} />
                    )}
                </DialogContent>
            </Dialog>

        {/* MWL Prompt */}
        {mwlEnabled && (
            <Dialog open={!!mwlPromptDialog} onClose={() => setMwlPromptDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {mwlPromptDialog?.studies?.length > 1
                        ? `Send ${mwlPromptDialog.studies.length} studies to Modality Worklist?`
                        : 'Send to Modality Worklist?'}
                </DialogTitle>
                <DialogContent>
                    <ModalityWorklistFields
                        study={mwlPromptDialog?.studies?.[0] || {}}
                        required
                        onMWLChange={setMwlData}
                        initialData={mwlInitialData}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMwlPromptDialog(null)}>Skip</Button>
                    <Button variant="contained" onClick={() => {
                        for (const s of mwlPromptDialog.studies)
                            MwlEdit(s.id, { ...mwlData, aetitle }).catch(() => {})
                        setMwlPromptDialog(null)
                    }}>Send to MWL</Button>
                </DialogActions>
            </Dialog>
        )}

        {/* Bulk Mismatch Warning */}
        <Dialog open={!!bulkWarnDialog} onClose={() => setBulkWarnDialog(null)} maxWidth="xs">
            <DialogTitle>Clinic Mismatch</DialogTitle>
            <DialogContent>
                <Alert severity="warning">
                    {bulkWarnDialog?.mismatchCount} of the selected studies are associated with a different clinic than <strong>{bulkWarnDialog?.slot?.childOrg?.name || 'this clinic'}</strong>. Schedule all anyway?
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setBulkWarnDialog(null)}>Cancel</Button>
                <Button variant="contained" color="warning" onClick={async () => {
                    const { studyIds, studies, slot, dateStr } = bulkWarnDialog
                    setBulkWarnDialog(null)
                    await executeBulkBook(studyIds, studies, slot, String(slot._id || slot.id || slot.slotId), dateStr)
                }}>Schedule Anyway</Button>
            </DialogActions>
        </Dialog>

        {/* Bulk Capacity Error */}
        <Dialog open={!!bulkErrorDialog} onClose={() => setBulkErrorDialog(null)} maxWidth="xs">
            <DialogTitle>Cannot Schedule</DialogTitle>
            <DialogContent><Alert severity="error">{bulkErrorDialog}</Alert></DialogContent>
            <DialogActions><Button onClick={() => setBulkErrorDialog(null)}>OK</Button></DialogActions>
        </Dialog>

        {canEdit && <>
            {/* Day Max */}
            <Dialog open={!!dayMaxDialog} onClose={() => setDayMaxDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Day Maximum</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Max Cases Per Day (blank = unlimited)" type="number" fullWidth
                            value={dayMaxDialog?.currentMax ?? ''} onChange={e => setDayMaxDialog(d => ({ ...d, currentMax: e.target.value }))} />
                        <FormControlLabel control={<Switch checked={dayMaxRecurring} onChange={e => setDayMaxRecurring(e.target.checked)} />} label="Recurring" />
                        {dayMaxRecurring && <RecurrenceEditor form={dayMaxRecForm} setForm={setDayMaxRecForm} startDate={dayMaxDialog?.date}
                            onStartDateChange={val => { const dow = utcDow(val); setDayMaxDialog(s => ({ ...s, date: val, dayOfWeek: dow })); setDayMaxRecForm(f => ({ ...f, daysOfWeek: [dow], dayOfMonth: utcDayOfMonth(val), weekOfMonth: getWeekOfMonth(val) })) }} />}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDayMaxDialog(null)}>Cancel</Button>
                    <Button variant="contained" onClick={saveDayMax}>{dayMaxRecurring ? 'Save Series' : 'Save This Day Only'}</Button>
                </DialogActions>
            </Dialog>
        </>}
        </>)
}

export default ScheduleCalendar
